import { BOOKING_DEFAULTS } from './booking';

/**
 * Цены по умолчанию (BLUEPRINT 3.6). Деньги — в копейках.
 * Боевые значения живут в таблице `Setting` под ключами `SETTING_KEYS`; это только дефолты для сида и фолбэка.
 */
export const PRICING_DEFAULTS = {
  /** Сезон из 6 сборов */
  seasonDefaultKopecks: 750_000,
  /** Пробный сбор в существующем отряде */
  trialKopecks: 60_000,
  /** Замена на один сбор */
  substituteKopecks: 110_000,
  /** Скидка продления за 7 дней до конца сезона, % */
  renewalDiscountPct: 15,
  /** Реферальный бонус обоим на следующий заказ */
  referralBonusKopecks: 50_000,
  /** Скидка капитану, % (100 = бесплатно) */
  captainDiscountPct: 50,
  currency: 'RUB',
} as const;

export type PricingDefaults = typeof PRICING_DEFAULTS;

/** Платежи (Stage 7): правила гарантии набора. Параметры чека 54-ФЗ (СНО, НДС) — в конфиге API (`ROBOKASSA_SNO/TAX`). */
export const PAYMENT_DEFAULTS = {
  /** Сколько раз ops может продлить дедлайн гарантии набора */
  deadlineExtensions: 1,
  /** На сколько дней максимум продлевается дедлайн за один раз */
  deadlineExtendMaxDays: 14,
} as const;

/** Ключи таблицы `Setting` (ключ → JSON). */
export const SETTING_KEYS = {
  pricingSeasonDefault: 'pricing.seasonDefault',
  pricingTrial: 'pricing.trial',
  pricingSubstitute: 'pricing.substitute',
  pricingRenewalDiscount: 'pricing.renewalDiscount',
  pricingReferralBonus: 'pricing.referralBonus',
  pricingCaptainDiscount: 'pricing.captainDiscount',
  squadSaleWindowDays: 'squad.saleWindowDays',
  squadMinToLaunchRatio: 'squad.minToLaunchRatio',
  squadDefaultCapacity: 'squad.defaultCapacity',
  squadReserveInviteTtlDays: 'squad.reserveInviteTtlDays',
  squadMaxPoolApplications: 'squad.maxPoolApplications',
  activityProposalSizeMin: 'activity.proposalSizeMin',
  activityProposalSizeMax: 'activity.proposalSizeMax',
  /** Фиче-флаги витрины открытых мест и пробного сбора (Stage 11) */
  featureOpenSeats: 'feature.openSeats',
  featureTrial: 'feature.trial',
  /** Сколько раз ops может продлить дедлайн гарантии набора (Stage 7) — по умолчанию один */
  paymentDeadlineExtensions: 'payment.deadlineExtensions',
  /** «Мои сборы» и заявка из нескольких разовых мест (Stage 17, ADR-35), по умолчанию выключено */
  featureMyGatherings: 'feature.myGatherings',
  /**
   * Города выкатки «Моих сборов» (Stage 17, задача 9; ADR-47): список `City.slug`; пустой — все
   * города. Действует только при включённом `feature.myGatherings`
   */
  featureMyGatheringsCities: 'feature.myGatheringsCities',
  /** Цена разового места = доля сезона × множитель (Stage 17) */
  pricingSingleRatio: 'pricing.singleRatio',
  /** Срок зачёта разовых мест в сезон, дней (Stage 17) */
  pricingSingleCreditDays: 'pricing.singleCreditDays',
  bookingHoldMinutes: 'booking.holdMinutes',
  bookingMaxItems: 'booking.maxItems',
  bookingNoSeasonMax: 'booking.noSeasonMax',
  bookingNoSeasonWindowDays: 'booking.noSeasonWindowDays',
  bookingSingleShareMax: 'booking.singleShareMax',
  bookingSingleSaleDays: 'booking.singleSaleDays',
  bookingCancelFullRefundHours: 'booking.cancelFullRefundHours',
} as const;

export type SettingKey = (typeof SETTING_KEYS)[keyof typeof SETTING_KEYS];

/**
 * Тип значения и дефолт каждого ключа `Setting` — для `admin.settings` (Stage 5):
 * API проверяет значение по `kind`, админка рисует подходящее поле. Подписи — в texts.ts.
 * `ratio` — доля в (0, 1]; `multiplier` — множитель в (0, `SETTING_MULTIPLIER_MAX`], может быть
 * больше 1 (цена разового места — 1,3 доли сезона, Stage 17). `cities` — список `City.slug`
 * (города выкатки флага, ADR-47).
 */
export type SettingKind =
  'kopecks' | 'percent' | 'ratio' | 'multiplier' | 'int' | 'boolean' | 'cities';
export type SettingValue = number | boolean | string[];
export type SettingGroup = 'pricing' | 'squad' | 'activity' | 'feature' | 'payment' | 'booking';

/** Верхняя граница значения вида `multiplier`: защита от опечатки «13» вместо «1,3». */
export const SETTING_MULTIPLIER_MAX = 5;

export interface SettingDefinition {
  key: SettingKey;
  kind: SettingKind;
  group: SettingGroup;
  default: SettingValue;
}

export const SETTING_DEFINITIONS: readonly SettingDefinition[] = [
  { key: SETTING_KEYS.pricingSeasonDefault, kind: 'kopecks', group: 'pricing', default: PRICING_DEFAULTS.seasonDefaultKopecks },
  { key: SETTING_KEYS.pricingTrial, kind: 'kopecks', group: 'pricing', default: PRICING_DEFAULTS.trialKopecks },
  { key: SETTING_KEYS.pricingSubstitute, kind: 'kopecks', group: 'pricing', default: PRICING_DEFAULTS.substituteKopecks },
  { key: SETTING_KEYS.pricingRenewalDiscount, kind: 'percent', group: 'pricing', default: PRICING_DEFAULTS.renewalDiscountPct },
  { key: SETTING_KEYS.pricingReferralBonus, kind: 'kopecks', group: 'pricing', default: PRICING_DEFAULTS.referralBonusKopecks },
  { key: SETTING_KEYS.pricingCaptainDiscount, kind: 'percent', group: 'pricing', default: PRICING_DEFAULTS.captainDiscountPct },
  { key: SETTING_KEYS.squadSaleWindowDays, kind: 'int', group: 'squad', default: 14 },
  { key: SETTING_KEYS.squadMinToLaunchRatio, kind: 'ratio', group: 'squad', default: 0.75 },
  { key: SETTING_KEYS.squadDefaultCapacity, kind: 'int', group: 'squad', default: 8 },
  { key: SETTING_KEYS.squadReserveInviteTtlDays, kind: 'int', group: 'squad', default: 7 },
  { key: SETTING_KEYS.squadMaxPoolApplications, kind: 'int', group: 'squad', default: 2 },
  { key: SETTING_KEYS.activityProposalSizeMin, kind: 'int', group: 'activity', default: 2 },
  { key: SETTING_KEYS.activityProposalSizeMax, kind: 'int', group: 'activity', default: 16 },
  { key: SETTING_KEYS.featureOpenSeats, kind: 'boolean', group: 'feature', default: true },
  { key: SETTING_KEYS.featureTrial, kind: 'boolean', group: 'feature', default: true },
  { key: SETTING_KEYS.paymentDeadlineExtensions, kind: 'int', group: 'payment', default: PAYMENT_DEFAULTS.deadlineExtensions },
  // Stage 17, «Мои сборы» (ADR-35)
  { key: SETTING_KEYS.featureMyGatherings, kind: 'boolean', group: 'feature', default: false },
  { key: SETTING_KEYS.featureMyGatheringsCities, kind: 'cities', group: 'feature', default: [] },
  { key: SETTING_KEYS.pricingSingleRatio, kind: 'multiplier', group: 'pricing', default: BOOKING_DEFAULTS.singleRatio },
  { key: SETTING_KEYS.pricingSingleCreditDays, kind: 'int', group: 'pricing', default: BOOKING_DEFAULTS.singleCreditDays },
  { key: SETTING_KEYS.bookingHoldMinutes, kind: 'int', group: 'booking', default: BOOKING_DEFAULTS.holdMinutes },
  { key: SETTING_KEYS.bookingMaxItems, kind: 'int', group: 'booking', default: BOOKING_DEFAULTS.maxItems },
  { key: SETTING_KEYS.bookingNoSeasonMax, kind: 'int', group: 'booking', default: BOOKING_DEFAULTS.noSeasonMax },
  { key: SETTING_KEYS.bookingNoSeasonWindowDays, kind: 'int', group: 'booking', default: BOOKING_DEFAULTS.noSeasonWindowDays },
  { key: SETTING_KEYS.bookingSingleShareMax, kind: 'ratio', group: 'booking', default: BOOKING_DEFAULTS.singleShareMax },
  { key: SETTING_KEYS.bookingSingleSaleDays, kind: 'int', group: 'booking', default: BOOKING_DEFAULTS.singleSaleDays },
  { key: SETTING_KEYS.bookingCancelFullRefundHours, kind: 'int', group: 'booking', default: BOOKING_DEFAULTS.cancelFullRefundHours },
];

export const SETTING_DEFINITION_BY_KEY: Readonly<Record<SettingKey, SettingDefinition>> =
  Object.fromEntries(SETTING_DEFINITIONS.map((d) => [d.key, d])) as Record<
    SettingKey,
    SettingDefinition
  >;
